import { parse, type Node } from "acorn";
import { ancestor } from "acorn-walk";
import MagicString from "magic-string";

// PRD FR-16, second layer: rewrite student JS so every loop stops itself after a time budget.
// Each loop gets a start time; the body checks it on every pass and calls __blLoopStop
// (defined by the bridge), which throws a readable error the console panel shows.
// No newlines are added, so line numbers in errors still match the student's file.

const LOOP_TYPES = ["WhileStatement", "DoWhileStatement", "ForStatement", "ForInStatement", "ForOfStatement"] as const;

interface LoopSite {
  wrapStart: number;
  wrapEnd: number;
  body: Node;
  line: number;
}

export interface LoopGuardResult {
  code: string;
  guardedLoops: number;
  /** False when the code didn't parse; it is returned unchanged so the browser reports the syntax error. */
  parsed: boolean;
}

function parseScript(code: string) {
  const options = { ecmaVersion: "latest" as const, locations: true, allowHashBang: true };
  try {
    return parse(code, { ...options, sourceType: "script" });
  } catch {
    try {
      return parse(code, { ...options, sourceType: "module" });
    } catch {
      return null;
    }
  }
}

export function addLoopGuards(code: string, fileName = "script", budgetMs = 1000): LoopGuardResult {
  const ast = parseScript(code);
  if (!ast) return { code, guardedLoops: 0, parsed: false };

  const sites: LoopSite[] = [];
  const visit = (node: Node, _state: unknown, ancestors: Node[]) => {
    // A labeled loop is wrapped together with its label so `break label` keeps working.
    let wrap: Node = node;
    for (let i = ancestors.length - 2; i >= 0; i--) {
      const parent = ancestors[i] as Node & { body?: Node };
      if (parent.type === "LabeledStatement" && parent.body === wrap) wrap = parent;
      else break;
    }
    const loop = node as Node & { body: Node; loc?: { start: { line: number } } };
    sites.push({ wrapStart: wrap.start, wrapEnd: wrap.end, body: loop.body, line: loop.loc?.start.line ?? 0 });
  };
  ancestor(ast, Object.fromEntries(LOOP_TYPES.map((t) => [t, visit])));
  if (sites.length === 0) return { code, guardedLoops: 0, parsed: true };

  // Outer loops first, so openings at the same position nest correctly.
  sites.sort((a, b) => a.wrapStart - b.wrapStart || b.wrapEnd - a.wrapEnd);
  const s = new MagicString(code);
  const file = JSON.stringify(fileName);
  sites.forEach((site, i) => {
    const t = `__blT${i}`;
    // __blNow is the bridge's real clock, which a play-test's t.speed doesn't speed up.
    const now = `(typeof __blNow==="function"?__blNow:Date.now)()`;
    const check = `if(${now}-${t}>${budgetMs})__blLoopStop(${file},${site.line});`;
    s.appendRight(site.wrapStart, `{const ${t}=${now};`);
    s.prependLeft(site.wrapEnd, "}");
    if (site.body.type === "BlockStatement") {
      s.appendRight(site.body.start + 1, check);
    } else {
      s.appendRight(site.body.start, `{${check}`);
      s.prependLeft(site.body.end, "}");
    }
  });
  return { code: s.toString(), guardedLoops: sites.length, parsed: true };
}
